import React, { useEffect, useRef, useState } from 'react';
import { updateVisibility } from '../../services/plannerApi';
import { getItineraryShareInfo, recordShare, regenerateShareToken } from '../../services/itineraryChatApi';
import { fetchApi } from '../../services/apiBase';
import { trackEvent } from '../../hooks/useAnalytics';
import {
  checkPublishOnVisit, clearPublishPending, markPublishPending, onPublishStatus, watchPublish, type ShareStatus,
} from '../../utils/publishWatch';
import { checkShareStatus, type PublishStatus } from '../../services/plansApi';
import { outcomeLines, sheetDoor } from '../../utils/publishOutcome';
import './ShareSheet.css';

interface ShareSheetProps {
  itineraryId: number;
  title: string;
  destination: string;
  currentVisibility: string;
  currentSlug?: string | null;
  isCompleted?: boolean;
  onClose: () => void;
  onVisibilityChange?: (visibility: string, slug: string | null) => void;
}

const InviteSection: React.FC<{ itineraryId: number; planSlug?: string | null }> = ({ itineraryId, planSlug }) => {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string[]>([]);
  const [error, setError] = useState('');

  const isPhone = /^[+\d][\d\s\-()]{6,}$/.test(input.trim());
  const isEmail = input.includes('@');

  const handleSend = async () => {
    const val = input.trim();
    if (!val || sending) return;
    setSending(true);
    setError('');
    try {
      const body = isPhone ? { phone: val } : { email: val };
      const result = await fetchApi<{ send_accepted?: boolean }>(`/api/planner/itineraries/${itineraryId}/invite/`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      // Funnel step: plan → shared — a direct invite is a stronger share
      // signal than a copied link, so it counts under the same event.
      //
      // Gated on the server's `send_accepted` flag rather than on a 2xx: the
      // endpoint creates the invite row and returns 201 even when the email
      // provider rejects the send, and its phone branch is an SMS stub that
      // sends nothing. This measures the share ACT — the provider took the
      // message — not receipt, which the acting side cannot observe (a later
      // bounce is invisible here). When SMS ships and reports acceptance,
      // phone invites start counting with no change.
      if (result?.send_accepted) {
        trackEvent('plan_shared', {
          method: 'direct_invite',
          itinerary_id: itineraryId,
          plan_slug: planSlug || undefined,
          channel: isPhone ? 'phone' : 'email',
        });
      }
      setSent(prev => [...prev, val]);
      setInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="share-sheet-invite">
      <div className="share-sheet-invite-title">Invite people</div>
      <div className="share-sheet-invite-desc">Share with up to 5 people by email or phone</div>
      <div className="share-sheet-invite-row">
        <input
          className="share-sheet-invite-input"
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Email or phone number"
          onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
        />
        <button className="share-sheet-invite-btn" onClick={handleSend} disabled={(!isEmail && !isPhone) || sending}>
          {sending ? '...' : 'Send'}
        </button>
      </div>
      {error && <p className="share-sheet-invite-error">{error}</p>}
      {sent.map(e => (
        <div key={e} className="share-sheet-invite-sent">Sent to {e}</div>
      ))}
    </div>
  );
};

const ShareSheet: React.FC<ShareSheetProps> = ({
  itineraryId,
  title,
  destination,
  currentVisibility,
  currentSlug,
  isCompleted,
  onClose,
  onVisibilityChange,
}) => {
  const [visibility, setVisibility] = useState(currentVisibility);
  const [slug, setSlug] = useState(currentSlug || null);
  // WHICH PLAN THE "Copied!" BELONGS TO, not a bare boolean — paired with its
  // id for the reason `fetched` below is, and because the feedback is cleared
  // by a TIMER that outlives the plan it was started for.
  //
  // Both directions were wrong while this was a boolean (external r810). Plan
  // A's 2s timer fired while plan B was on screen and cleared B's "Copied!"
  // early; and had the timer simply been suppressed instead, A's "Copied!"
  // would have stayed lit under B forever, because the id-keyed effect below
  // resets the notes and not this. Keyed by id, a prop change hides it in the
  // same render — the mismatch is unrepresentable rather than merely unlikely
  // — and the timer clears only its own.
  const [copied, setCopied] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // THIS SHEET NEVER HANDLES A TOKEN, AND TAKES NO PROP THAT COULD HAND IT
  // ONE. It asks the owner-only share endpoint for a finished URL.
  //
  // Both halves matter. No plan payload carries `share_token` any more, so
  // the endpoint is the only source — and asking it is what RECORDS the share
  // (`_record_share`), which is what makes the readers admit the recipient. A
  // sheet that could accept a token from a parent would build a URL for a
  // plan the server never saw shared, and the recipient would be refused —
  // which is the bug this slice exists to close. The prop is deleted rather
  // than left optional, because "nothing passes it today" is not a property
  // anyone maintains.
  //
  // The URL comes back whole rather than being rebuilt here: the server owns
  // the site origin and the `?from=` funnel tag, and two builders drift.
  // THE URL IS STORED WITH THE PLAN IT BELONGS TO.
  //
  // Holding a bare URL let the previous plan's link stay live while the next
  // plan's request was in flight — and if that request failed, the error path
  // only moved `linkState`, so the old URL stayed "ready" indefinitely. The
  // user could then copy a link to a plan they were no longer looking at,
  // which for a capability is the worst possible kind of stale render.
  //
  // Pairing the URL with its id makes the mismatch unrepresentable: a URL is
  // only used when its `for` matches the plan on screen, so a change clears
  // it in the same render rather than in an effect that may never run.
  const [fetched, setFetched] = useState<{ for: number; url: string } | null>(null);
  const [linkState, setLinkState] = useState<'ready' | 'loading' | 'unavailable'>('loading');
  // The share was handed out and the server did not write it down. Said out
  // loud rather than swallowed: the recipient of THIS copy will be refused by
  // every reader, and copying again is the whole fix.
  const [recordFailed, setRecordFailed] = useState(false);
  // Revoking is a two-step inline confirm, because it cannot be undone: every
  // link already sent stops working the moment it lands.
  const [revokeStep, setRevokeStep] = useState<'idle' | 'confirm'>('idle');
  const [revokeNote, setRevokeNote] = useState<'' | 'revoked' | 'failed'>('');
  const [revoking, setRevoking] = useState(false);
  // WHICH PLAN THE SHEET IS SHOWING RIGHT NOW. Every handler below is async,
  // and this component is re-pointed at another plan by a prop change rather
  // than remounted — so a copy or a revoke started for plan A can resolve
  // while plan B is on screen, and an unguarded `setRevokeNote('revoked')`
  // then tells the owner that B's link is dead. It is not. Same failure the
  // fetch effect's `live`/`fetched.for` pairing exists to prevent; the
  // handlers need their own because they outlive no effect.
  //
  // Written in the id-keyed effect below, which commits before any promise
  // started under the previous id can resolve.
  const showing = useRef(itineraryId);
  useEffect(() => {
    let live = true;
    showing.current = itineraryId;
    setLinkState('loading');
    // Every note belongs to the plan it was raised for.
    setRecordFailed(false);
    setRevokeStep('idle');
    setRevokeNote('');
    (async () => {
      try {
        // THE DISPLAY FETCH RECORDS NOTHING (`record: false`). Holding a
        // URL in an open sheet is not handing it out, and this sheet is
        // opened to press "Publish to Explore" at least as often as to share
        // anything — every one of those opens used to record a share, and
        // since slice 1 a recorded share is what opens a `private` plan to
        // its token. The act records instead: `handleCopyLink` and
        // `handleNativeShare` below, and the invite path server-side.
        const info = await getItineraryShareInfo(itineraryId, { record: false });
        if (!live) return;
        if (info && typeof info.share_url === 'string' && info.share_url) {
          setFetched({ for: itineraryId, url: info.share_url });
          setLinkState('ready');
        } else {
          setLinkState('unavailable');
        }
      } catch {
        if (live) setLinkState('unavailable');
      }
    })();
    return () => { live = false; };
  }, [itineraryId]);

  const shareUrl = fetched && fetched.for === itineraryId ? fetched.url : null;

  // The endpoint returns the token page, always — not `/plans/p/<slug>`,
  // even when published: the slug page renders only a public itinerary that
  // no published CommunityPlan shadows, while a `link` or once-shared
  // `private` plan has a slug and must not open by it. The token page renders
  // and forks every plan this sheet can share, records the share, and carries
  // the `?from=` tag the receiving side of the funnel filters on (B-4).
  const linkReady = Boolean(shareUrl);
  const linkUnavailable = !shareUrl && linkState === 'unavailable';
  const linkLoading = !shareUrl && !linkUnavailable;

  // The on-next-visit leg of the observed publish: a toggle left "staged, not
  // yet observed" (the tab was closed, or the poll gave up) is checked once.
  useEffect(() => {
    void checkPublishOnVisit(itineraryId, slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId]);

  // Increment 5: the outcome line, rendered from the LAST status fetched —
  // an UNCONDITIONAL read on open (`checkPublishOnVisit` reads only behind a
  // marker, and the line would be blank on most opens), then every poll
  // result through the observer.
  const [status, setStatus] = useState<ShareStatus | null>(null);
  const [showProblems, setShowProblems] = useState(false);
  useEffect(() => {
    let live = true;
    checkShareStatus(itineraryId).then((s) => { if (live) setStatus(s); }).catch(() => { /* the line stays blank */ });
    const off = onPublishStatus(itineraryId, (s) => { if (live) setStatus(s); });
    return () => { live = false; off(); };
  }, [itineraryId]);
  const refreshStatus = () => {
    checkShareStatus(itineraryId).then(setStatus).catch(() => { /* the next poll will */ });
  };

  const handleTogglePublic = async () => {
    const newVis = visibility === 'public' ? 'private' : 'public';
    setSaving(true);
    try {
      const result = await updateVisibility(itineraryId, newVis);
      setVisibility(result.visibility);
      setSlug(result.slug);
      onVisibilityChange?.(result.visibility, result.slug);
      // Phase 2: the ack is not publication. A public toggle stages a
      // candidate the server checks first; the share is COUNTED when the
      // publish is observed to have landed, once per (itinerary, promoted_at).
      if (result.visibility === 'public') {
        markPublishPending(itineraryId);
        void watchPublish(itineraryId, result.slug);
      } else {
        clearPublishPending(itineraryId);
      }
      // The WRITE's own answer first — a follow-up GET can fail and leave the
      // sheet saying "In Explore" beside an off toggle (external round 484).
      // The GET stays as the path for servers that send no `publish`.
      if (result.publish) setStatus((prev) => ({ shared: prev?.shared ?? false, edit_count: prev?.edit_count ?? 0, publish: result.publish as PublishStatus }));
      else refreshStatus();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  /** *Try again* after a failed or expired candidate (decision 1): the
   *  EXPLICIT PATCH `public` — never the toggle, which on a still-public plan
   *  would flip it to private, a withdrawal. Offered only while the plan IS
   *  public (`sheetDoor`), so it is never the slug-minting PATCH on a plan
   *  shared from a completed trip. The view stages afresh: a failed candidate
   *  is not live, and only live candidates short-circuit. */
  const handleTryAgain = async () => {
    setSaving(true);
    try {
      const result = await updateVisibility(itineraryId, 'public');
      setVisibility(result.visibility);
      setSlug(result.slug);
      onVisibilityChange?.(result.visibility, result.slug);
      markPublishPending(itineraryId);
      void watchPublish(itineraryId, result.slug);
      if (result.publish) setStatus((prev) => ({ shared: prev?.shared ?? false, edit_count: prev?.edit_count ?? 0, publish: result.publish as PublishStatus }));
      else refreshStatus();
    } catch {
      // silent — the line still says what the server last said
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    const startedFor = itineraryId;
    // FIRED BEFORE THE COPY AND DELIBERATELY NOT AWAITED FIRST. Safari only
    // allows a clipboard write inside the gesture's own task, so an await
    // before `writeText` loses the copy entirely. The promise is caught here
    // and read after the copy, so neither an early return below nor a server
    // error becomes an unhandled rejection.
    const recorded = recordShare(itineraryId).then(() => true).catch(() => false);
    setRecordFailed(false);
    let handedOut = true;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(input);
      // NOTHING WAS COPIED, AND THE SHARE IS ALREADY RECORDED. Deliberate:
      // the record was fired before the copy because Safari allows the write
      // only inside the gesture's own task, so it cannot wait on an outcome
      // the browser reports this late. Over-recording errs the recoverable
      // way — a plan that opens to a link its owner nearly handed out, rather
      // than a plan that refuses a link its owner really did.
      handedOut = ok;
    }
    // NOTHING LEFT THE BUILDING, so there is nothing to say and nothing to
    // count. This is the only exit taken on a PROP CHANGE — the handler no
    // longer gives up because the sheet moved on. (It is not the only exit:
    // the failed-record branch below returns too, deliberately.)
    if (!handedOut) return;
    // THE FEEDBACK IS THE SHEET'S, THE EVENT IS THE PLAN'S, and they part
    // company here. A clipboard write can resolve after the sheet has been
    // re-pointed at another plan: rendering "Copied!" then would be a note
    // about the wrong plan, so the state write is guarded — but the copy of
    // plan A's link really happened, and the two returns that used to sit
    // here took `plan_shared` down with the feedback, under-counting the
    // funnel for exactly the users who move fastest (external r810).
    if (showing.current === startedFor) {
      setCopied(startedFor);
      // Clears only ITS OWN feedback: by the time this fires the sheet may be
      // showing another plan whose own copy is lit.
      setTimeout(
        () => setCopied((shown) => (shown === startedFor ? null : shown)),
        2000);
    }
    // A share the server did not record is not a share: the recipient of this
    // link will be refused. So the funnel event waits for the record, and
    // does not fire without it — an event for a link nobody can open would
    // make the share→fork number read better than the product works.
    if (!(await recorded)) {
      // The note belongs to the plan it was raised for; the EVENT below does
      // not — it carries plan A's id and reports a share that really
      // happened, so it is not suppressed by a prop change.
      if (showing.current === startedFor) setRecordFailed(true);
      return;
    }
    // Funnel step: plan → shared — reached only after a successful copy.
    // The link is the token page, whose plan_page_visit carries itinerary_id,
    // so that is the join key; plan_slug stays on the event as a public fact
    // about the plan, not as the join.
    //
    // `startedFor` rather than `itineraryId`, which is a LEGIBILITY change
    // and not a fix: the two are the same value inside any one invocation,
    // because props are captured per render and an in-flight handler keeps
    // the render's own const. (Asserted the hard way — the mutant swapping
    // one for the other survives, and is recorded as equivalent rather than
    // claimed as a kill.) It is spelled this way so that the plan this line
    // reports is visibly the plan the handler started on, beside the guards
    // that say the same thing about the state writes. `showing.current` is a
    // ref precisely because it must NOT behave like this.
    trackEvent('plan_shared', { method: 'copy_link', itinerary_id: startedFor, plan_slug: slug || undefined });
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    const startedFor = itineraryId;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${title} — ${destination}`,
          text: `Check out this plan on VizDay`,
          url: shareUrl,
        });
        // Recorded on the share call's SUCCESS — a cancelled sheet throws and
        // hands out nothing. No gesture constraint here, so this one waits.
        //
        // Guarded like every other write: the OS share sheet is open for as
        // long as the user leaves it open, which is ample time for the sheet
        // to be re-pointed at another plan, and clearing the failure line
        // would clear it for THAT plan.
        if (showing.current === startedFor) setRecordFailed(false);
        try {
          // `startedFor` rather than `itineraryId`: the same value, by the
          // same per-render capture as in `handleCopyLink` above, spelled so
          // the plan being RECORDED is visibly the plan that was shared.
          await recordShare(startedFor);
        } catch {
          if (showing.current === startedFor) setRecordFailed(true);
          return;
        }
        // `startedFor` here too, for the same reason and with the same
        // caveat: equivalent to `itineraryId`, chosen for legibility.
        trackEvent('plan_shared', { method: 'native_share', itinerary_id: startedFor, plan_slug: slug || undefined });
      } catch {
        // User cancelled
      }
    }
  };

  /** Revocation, which on this product is rotating the token: the server
   *  mints a new one and clears the recorded share in one statement, so a
   *  `private` plan closes to every token until it is shared again. The sheet
   *  then shows the NEW url — anything else would leave a revoked link on
   *  screen for the owner to copy. */
  const handleRevoke = async () => {
    if (revoking) return;
    const startedFor = itineraryId;
    setRevoking(true);
    setRevokeNote('');
    try {
      const result = await regenerateShareToken(itineraryId);
      if (!result || typeof result.share_url !== 'string' || !result.share_url) {
        throw new Error('no url');
      }
      // The sheet may have been re-pointed at another plan while this was in
      // flight. `setFetched` is already safe — it is paired with its id and
      // the render ignores a mismatch — but "Link revoked. The old link no
      // longer opens this plan." under the NEW plan is a lie about a live
      // link, so every write here is guarded.
      if (showing.current !== startedFor) return;
      setFetched({ for: itineraryId, url: result.share_url });
      setLinkState('ready');
      setRecordFailed(false);
      setRevokeStep('idle');
      setRevokeNote('revoked');
    } catch {
      if (showing.current === startedFor) setRevokeNote('failed');
    } finally {
      // NOT guarded: this is the sheet's own busy flag, not a note about a
      // plan, and leaving it set would disable the new plan's Revoke button.
      setRevoking(false);
    }
  };

  return (
    <div className="share-sheet-backdrop" onClick={onClose}>
      <div className="share-sheet" onClick={e => e.stopPropagation()}>
        <div className="share-sheet-handle" />

        <h3 className="share-sheet-title">{isCompleted ? 'Share how it went' : 'Share your plan'}</h3>
        <p className="share-sheet-subtitle">{isCompleted ? 'Anyone with this link can see your recap.' : 'Anyone with this link can view and use your plan.'}</p>

        {/* Copy link — or say plainly that there is none, rather than a
            sheet titled "Share" with nothing on it. */}
        {linkLoading && (
          <p className="share-sheet-link-loading">Preparing link…</p>
        )}
        {linkUnavailable && (
          <p className="share-sheet-link-unavailable">This plan can't be shared right now.</p>
        )}
        {linkReady && shareUrl && (
          <div className="share-sheet-link-row">
            <div className="share-sheet-link-url">{shareUrl}</div>
            <button className="share-sheet-copy-btn" onClick={handleCopyLink}>
              {copied === itineraryId ? 'Copied!' : 'Copy'}
            </button>
          </div>
        )}
        {recordFailed && (
          <p className="share-sheet-record-failed" role="status">
            We couldn't record this share. Copy the link again before sending it.
          </p>
        )}

        {/* Revoke — secondary, never the gold, and never one tap from the
            link it kills. Every link already sent stops working. */}
        {linkReady && shareUrl && (
          <div className="share-sheet-revoke">
            {revokeStep === 'idle' ? (
              <button
                type="button"
                className="share-sheet-revoke-btn"
                onClick={() => { setRevokeNote(''); setRevokeStep('confirm'); }}
              >
                Revoke link
              </button>
            ) : (
              <div className="share-sheet-revoke-confirm">
                <span className="share-sheet-revoke-prompt">
                  Anyone with the old link will lose access. Revoke?
                </span>
                <button
                  type="button"
                  className="share-sheet-revoke-yes"
                  onClick={handleRevoke}
                  disabled={revoking}
                >
                  Revoke
                </button>
                <button
                  type="button"
                  className="share-sheet-revoke-no"
                  onClick={() => setRevokeStep('idle')}
                  disabled={revoking}
                >
                  Keep
                </button>
              </div>
            )}
            {revokeNote === 'revoked' && (
              <p className="share-sheet-revoke-note" role="status">
                Link revoked. The old link no longer opens this plan.
              </p>
            )}
            {revokeNote === 'failed' && (
              <p className="share-sheet-revoke-note share-sheet-revoke-note--failed" role="status">
                Couldn't revoke the link. Try again.
              </p>
            )}
          </div>
        )}

        {/* Share targets */}
        {shareUrl && (
          <div className="share-sheet-targets">
            {typeof navigator.share === 'function' && (
              <button className="share-sheet-target" onClick={handleNativeShare}>
                <span className="share-sheet-target-icon">📤</span>
                <span className="share-sheet-target-label">Share</span>
              </button>
            )}
          </div>
        )}

        {/* OG preview */}
        {shareUrl && (
          <div className="share-sheet-preview">
            <div className="share-sheet-preview-label">LINK PREVIEW</div>
            <div className="share-sheet-preview-card">
              <div className="share-sheet-preview-body">
                <div className="share-sheet-preview-site">vizday.com</div>
                <div className="share-sheet-preview-title">{title} — {destination}</div>
              </div>
            </div>
          </div>
        )}

        {/* Publish to Explore: the toggle, and where the publish stands */}
        {(() => {
          const publish = status?.publish;
          const lines = publish ? outcomeLines(publish, { visibility }) : [];
          const door = sheetDoor(publish, visibility);
          const problems = publish?.problems || [];
          const fixable = lines.some((l) => l.action === 'fix') && problems.length > 0;
          return (
            <div className="share-sheet-publish" data-door={door}>
              <div className="share-sheet-publish-info">
                <div className="share-sheet-publish-title">Publish to Explore</div>
                {lines.length === 0 ? (
                  <div className="share-sheet-publish-desc">Discoverable in search and community</div>
                ) : lines.map((line) => (
                  <div key={line.text} className={`share-sheet-publish-desc share-sheet-publish-desc--${line.tone}`} role="status">
                    {line.text}
                  </div>
                ))}
                {fixable && (
                  <button type="button" className="share-sheet-publish-link" onClick={() => setShowProblems((v) => !v)}>
                    {showProblems ? 'Hide' : 'See what to fix'}
                  </button>
                )}
                {fixable && showProblems && (
                  <ul className="share-sheet-problems">
                    {problems.map((pr) => (
                      <li key={pr.check}><strong>{pr.label}</strong>{pr.detail ? `: ${pr.detail}` : ''}</li>
                    ))}
                  </ul>
                )}
              </div>
              {door === 'try_again' && (
                <button type="button" className="share-sheet-try-again" onClick={handleTryAgain} disabled={saving}>
                  Try again
                </button>
              )}
              {(door === 'toggle' || door === 'unknown') && (
                <button
                  className={`share-sheet-toggle ${visibility === 'public' ? 'share-sheet-toggle--on' : ''}`}
                  onClick={handleTogglePublic}
                  // Disabled until the status is known: on a public plan this
                  // button is the WITHDRAWAL, and before the read there is no
                  // way to tell a healthy publish from a failed one that wants
                  // *Try again* (external round 484).
                  disabled={saving || door === 'unknown'}
                  aria-label={visibility === 'public' ? 'Remove from Explore' : 'Publish to Explore'}
                >
                  <div className="share-sheet-toggle-knob" />
                </button>
              )}
            </div>
          );
        })()}


        {/* Invite people */}
        <InviteSection itineraryId={itineraryId} planSlug={slug} />

        <button className="share-sheet-done" onClick={onClose}>Done</button>
      </div>
    </div>
  );
};

export default ShareSheet;
